import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, UploadCloud, Check, Music, Image as ImageIcon, Save, Film, Video, Zap, ExternalLink, Trash2, Link2, Copy, QrCode, Languages, CornerDownRight, Globe } from "lucide-react";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import TriggerOverlay from "@/components/TriggerOverlay";
import { PromoClip } from "./PromoClip";
import { LANGS as SEC_LANGS, LANG_LABELS as SEC_LANG_LABELS } from "@/i18n";
import { getProject, patchProject, uploadAsset, readAudioDuration, formatApiErrorDetail,
  setTriggerDefault, setTriggerOverride, deleteTriggerOverride, getTriggerStats,
  fetchQrBlob, triggerDownload, translateSections, removeSectionImage, removeSectionVideo, removeClosingImage,
  setPreviewWindow, resetTestData, getPreviewStreamUrl, warmPreviewClip, createSmartLink,
  getDomain, connectDomain, verifyDomain, disconnectDomain } from "./adminApi";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LINK_TYPES = ["story_link", "direct_link", "profile_link"];

const TRIGGER_TYPES = ["none", "comment_keyword", "dm_keyword", "story_link", "profile_link", "search_profile", "direct_link"];
const TONES = ["cinematic_noir", "deep_blue", "crimson_minimal", "ghost"];
const VARIANTS = ["end_card", "mid_signature", "soft_cta"];
const EMPTY_TRIGGER = { project_title: "", supporting_line: "", instruction_line: "", trigger_type: "none", brand_signature: "FANLY", tone_preset: "cinematic_noir", variant: "end_card" };

function UploadCard({ title, hint, kind, accept, slug, isAudio, current, onDone, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setDone(false);
    try {
      const extra = {};
      if (isAudio) {
        const d = await readAudioDuration(file);
        if (d) extra.duration_sec = d;
      }
      const data = await uploadAsset(slug, kind, file, extra);
      setDone(true);
      toast.success(`${title} uploaded`);
      onDone && onDone(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="border border-white/10 rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {isAudio ? <Music className="w-4 h-4 text-[#FF2A2A]" /> : <ImageIcon className="w-4 h-4 text-[#FF2A2A]" />}
            <h4 className="font-semibold">{title}</h4>
          </div>
          <p className="font-mono-x text-[11px] text-[#737373] leading-relaxed">{hint}</p>
          <p className="mt-2 font-mono-x text-[10px] tracking-widest uppercase">
            {current === false ? (
              <span className="text-[#FF2A2A]">Placeholder — replace with real asset</span>
            ) : current === true ? (
              <span className="text-emerald-400">Real asset uploaded</span>
            ) : null}
          </p>
        </div>
        <button
          onClick={pick}
          disabled={busy}
          data-testid={`${testid}-btn`}
          className="shrink-0 flex items-center gap-2 border border-white/20 rounded-full px-4 py-2 text-sm hover:border-[#FF2A2A] transition-colors disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : done ? <Check className="w-4 h-4 text-emerald-400" /> : <UploadCloud className="w-4 h-4" />}
          {busy ? "Uploading" : "Upload"}
        </button>
      </div>
      <input ref={inputRef} type="file" accept={accept} onChange={onFile} className="hidden" />
    </div>
  );
}

function SectionVideoCard({ slug, sectionKey, video, onUploaded, onRemove }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const pick = () => inputRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setPct(0);
    try {
      const res = await uploadAsset(slug, `sectionvideo_${sectionKey}`, file, {}, (p) => setPct(p));
      if (res?.url) { onUploaded(res.url); toast.success("Video uploaded"); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Video upload failed");
    } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="border border-white/10 rounded-2xl p-5 space-y-4" data-testid={`section-video-block-${sectionKey}`}>
      <div className="flex items-center gap-2 mb-1">
        <Video className="w-4 h-4 text-[#FF2A2A]" />
        <h4 className="font-semibold">Section video</h4>
      </div>
      <p className="font-mono-x text-[11px] text-[#737373] leading-relaxed">Recommended: 16:9 — 1920×1080. MP4, MOV or WebM.</p>
      {video && (
        <div className="relative w-full overflow-hidden rounded-xl border border-white/10 bg-black" style={{ aspectRatio: "16 / 9" }} data-testid={`section-video-frame-${sectionKey}`}>
          <video src={video ? video + "#t=0.1" : video} controls playsInline preload="metadata" className="absolute inset-0 w-full h-full object-contain bg-black" />
        </div>
      )}
      {busy && (
        <div data-testid={`section-video-progress-${sectionKey}`}>
          <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-[#FF2A2A] transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-1 font-mono-x text-[10px] text-[#8a8a8a]">Uploading… {pct}%</p>
        </div>
      )}
      <div className="flex items-center gap-3">
        <button onClick={pick} disabled={busy} data-testid={`upload-section-video-${sectionKey}-btn`}
          className="flex items-center gap-2 border border-white/20 rounded-full px-4 py-2 text-sm hover:border-[#FF2A2A] transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
          {busy ? "Uploading" : video ? "Replace video" : "Upload video"}
        </button>
        {video && (
          <button onClick={onRemove} data-testid={`remove-section-video-${sectionKey}`}
            className="flex items-center gap-2 text-[#8a8a8a] hover:text-[#FF2A2A] text-sm transition-colors">
            <Trash2 className="w-4 h-4" /> Remove video
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="video/*,.mp4,.mov,.m4v,.webm,.ogv" onChange={onFile} className="hidden" />
    </div>
  );
}


export default function AdminProjectEditor({ tab = "release" }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [form, setForm] = useState({ name: "", artist: "", tagline: "", release_label: "" });
  const [sections, setSections] = useState([]);
  const [canvasOnLanding, setCanvasOnLanding] = useState(false);
  const [allowDownload, setAllowDownload] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [previewStart, setPreviewStart] = useState(0);
  const [startDraft, setStartDraft] = useState(null);
  const [previewEnd, setPreviewEnd] = useState(25);
  const [trackDur, setTrackDur] = useState(0);
  const [savingPreview, setSavingPreview] = useState(false);
  const previewTestRef = useRef(null);
  const [sc, setSc] = useState({ content_piece: "another-reel-01", platform: "instagram", trigger_type: "profile_link", hook: "DON'T STOP HERE", action: "HEAR THE NEXT 25 SEC", cta_start: 18, cta_end: 25, preview_mode: "selected_preview", continue_from: "01:01" });
  // Phase-1 simplified controls (map to the sc payload on generate)
  const [audioMode, setAudioMode] = useState("saved");   // saved | moment | video(BETA)
  const [scDur, setScDur] = useState(25);                 // moment/video preview length (sec)
  const [ctaMode, setCtaMode] = useState("last");         // last | entire
  const [ctaLast, setCtaLast] = useState(7);              // "during the last N seconds"
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [smartLink, setSmartLink] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [trig, setTrig] = useState(EMPTY_TRIGGER);
  const [trigCp, setTrigCp] = useState("");
  const [trigPlatform, setTrigPlatform] = useState("");
  const [trigOverrides, setTrigOverrides] = useState([]);
  const [trigSaving, setTrigSaving] = useState(false);
  const [trigStats, setTrigStats] = useState({});
  const [qrPreview, setQrPreview] = useState("");
  const [secLang, setSecLang] = useState("en");
  const [translating, setTranslating] = useState(false);
  const [closing, setClosing] = useState({ active: true, cta_enabled: true, title: "", body: "", cta_label: "", image: null });
  const [domain, setDomain] = useState(null);
  const [domainInput, setDomainInput] = useState("");
  const [domainBusy, setDomainBusy] = useState(false);
  const promoPlayRef = useRef(null);

  const load = async () => {
    try {
      const p = await getProject(slug);
      setProject(p);
      setForm({ name: p.name || "", artist: p.artist || "", tagline: p.tagline || "", release_label: p.release_label || "" });
      setSections((p.sections || []).map((s) => ({
        ...s,
        label_i18n: s.label_i18n || { en: s.label || "" },
        body_i18n: s.body_i18n || { en: s.body || "" },
      })));
      setCanvasOnLanding(!!p.canvas_on_landing);
      setAllowDownload(!!p.allow_download);
      setTestMode(!!p.test_mode);
      const pv = p.assets?.preview;
      if (pv) {
        setPreviewStart(pv.preview_start_sec || 0);
        setPreviewEnd(pv.preview_end_sec || pv.hard_stop_sec || Math.min(25, pv.duration_sec || 25));
        setTrackDur(Math.round(pv.duration_sec || pv.hard_stop_sec || 0));
      }
      const cb = p.closing_block || {};
      setClosing({
        active: cb.active !== false, cta_enabled: cb.cta_enabled !== false,
        title: cb.title || "What's Next", body: cb.body || "",
        cta_label: cb.cta_label || "NOTIFY ME WHEN THERE'S MORE", image: cb.image || null,
      });
      setTrigOverrides(p.trigger_overrides || []);
      setTrig({ ...EMPTY_TRIGGER, ...(p.trigger_default || {}) });
      setTrigCp(""); setTrigPlatform("");
      getTriggerStats(slug).then(setTrigStats).catch(() => {});
      getDomain(slug).then((d) => { setDomain(d); setDomainInput(d?.domain || ""); }).catch(() => {});
    } catch (e) {
      toast.error("Could not load project");
      if (e?.response?.status === 401) navigate("/admin/login");
    }
  };
  useEffect(() => { load(); }, [slug]);

  const doConnectDomain = async () => {
    const val = (domainInput || "").trim();
    if (!val) { toast.error("Enter a domain, e.g. listen.yourdomain.com"); return; }
    setDomainBusy(true);
    try {
      const d = await connectDomain(slug, val);
      setDomain(d);
      toast.success("Domain saved — add the DNS records, then verify");
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Could not connect domain"); }
    finally { setDomainBusy(false); }
  };
  const doVerifyDomain = async () => {
    setDomainBusy(true);
    try {
      const d = await verifyDomain(slug);
      setDomain(d);
      toast.success("Domain verified — HTTPS active");
      load();
    } catch (e) { setDomain((p) => ({ ...(p || {}), verification_status: "error" })); toast.error(formatApiErrorDetail(e) || "Not verified yet"); }
    finally { setDomainBusy(false); }
  };
  const doDisconnectDomain = async () => {
    setDomainBusy(true);
    try {
      await disconnectDomain(slug);
      setDomain({ verification_status: "not_configured" });
      setDomainInput("");
      toast.success("Domain disconnected");
    } catch (e) { toast.error("Could not disconnect"); }
    finally { setDomainBusy(false); }
  };

  const buildSectionsPayload = (secs) => secs.map((s) => ({
    key: s.key,
    label: s.label_i18n?.en || s.label || "",
    body: s.body_i18n?.en || s.body || "",
    display_type: s.display_type || "standard",
    media_type: s.media_type || "image",
    active: s.active !== false,
    label_i18n: s.label_i18n || {},
    body_i18n: s.body_i18n || {},
  }));

  const buildClosingPayload = () => ({
    active: closing.active !== false,
    cta_enabled: closing.cta_enabled !== false,
    title: closing.title || "What's Next",
    body: closing.body || "",
    cta_label: closing.cta_label || "NOTIFY ME WHEN THERE'S MORE",
  });

  const saveTexts = async () => {
    setSaving(true);
    try {
      await patchProject(slug, {
        ...form,
        canvas_on_landing: canvasOnLanding,
        allow_download: allowDownload,
        test_mode: testMode,
        sections: buildSectionsPayload(sections),
        closing_block: buildClosingPayload(),
      });
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doResetTestData = async () => {
    if (!window.confirm("Delete ALL Test-Mode data for this release? Real fans/analytics are NOT affected.")) return;
    try {
      const r = await resetTestData(slug);
      const d = r.deleted || {};
      toast.success(`Test data cleared: ${d.fans || 0} fans, ${d.events || 0} events, ${d.broadcasts || 0} broadcasts`);
    } catch (e) { toast.error("Reset failed"); }
  };

  const savePreviewWindow = async () => {
    const len = Number(previewEnd) - Number(previewStart);
    if (!(len >= 15 && len <= 30)) { toast.error("Preview must be 15–30 seconds"); return; }
    setSavingPreview(true);
    try {
      await setPreviewWindow(slug, { start_sec: Number(previewStart), end_sec: Number(previewEnd) });
      warmPreviewClip(slug);
      toast.success(`Preview set to ${len}s (${previewStart}s → ${previewEnd}s)`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not set preview");
    } finally { setSavingPreview(false); }
  };

  const scInstruction = () => (sc.trigger_type === "profile_link" ? "LINK IN PROFILE"
    : sc.trigger_type === "story_link" ? "Add as a Story link sticker"
    : sc.trigger_type === "direct_link" ? "Use as the clickable URL (ads / bio / surfaces)" : "");
  const scAutomation = sc.trigger_type === "comment_keyword" || sc.trigger_type === "dm_keyword";
  const mmssToSec = (s) => { const m = String(s || "").split(":"); return m.length === 2 ? (Number(m[0]) * 60 + Number(m[1])) : Number(s) || 0; };
  const secToMmss = (n) => { const s = Math.max(0, Math.round(Number(n) || 0)); return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`; };
  const genSmartLink = async (overwrite = false) => {
    if (scAutomation) { toast.error("AUTOMATION NOT CONNECTED"); return; }
    setGenBusy(true);
    try {
      // Map the simplified controls to the smart-link payload (single source of truth).
      const dur = Math.max(5, Math.min(60, Number(scDur) || 25));
      const ctaStart = ctaMode === "entire" ? 0 : Math.max(0, dur - (Number(ctaLast) || 7));
      const payload = {
        ...sc, variant: sc.content_piece, instruction: scInstruction(),
        preview_mode: audioMode === "saved" ? "selected_preview" : "continue_from_reel",
        audio_mode: audioMode, cta_start: ctaStart, cta_end: dur, duration_sec: dur,
        overwrite,
      };
      if (audioMode !== "saved") payload.continue_from_sec = mmssToSec(sc.continue_from);
      const r = await createSmartLink(slug, payload);
      setSmartLink(r);
      toast.success("Smart Link ready");
    } catch (e) {
      const d = e?.response?.data?.detail;
      // Existing link for this content piece / platform / trigger — confirm before replacing.
      if (e?.response?.status === 409 && d?.reason === "smart_link_exists") {
        const ok = window.confirm(`${d.message}\n\nReplace it?`);
        if (ok) { setGenBusy(false); return genSmartLink(true); }
        toast.message("Tip: change the Content piece to keep both links.");
      } else {
        toast.error(formatApiErrorDetail(d) || "Could not generate");
      }
    }
    finally { setGenBusy(false); }
  };
  // Fan-experience test URL (exactly what the fan will get) without generating a link
  const fanTestUrl = () => {
    const dur = Math.max(5, Math.min(60, Number(scDur) || 25));
    const pm = audioMode === "saved" ? "selected_preview" : "continue_from_reel";
    const base = `/${slug}?from=smart&pm=${pm}&dur=${dur}`;
    return audioMode === "saved" ? base : `${base}&cf=${mmssToSec(sc.continue_from)}`;
  };

  const playPreviewTest = async () => {
    try {
      const url = await getPreviewStreamUrl(slug);
      const a = previewTestRef.current;
      a.src = url;
      await a.play();
      try { a.currentTime = Number(previewStart); } catch (e) {}
      const stop = () => {
        if (a.currentTime >= Number(previewEnd)) { a.pause(); a.removeEventListener("timeupdate", stop); }
      };
      a.addEventListener("timeupdate", stop);
    } catch (e) { toast.error("Preview playback failed"); }
  };


  // Persist the whole section set right after a media upload so typed (but not yet
  // "Saved") lyrics/body/title are never lost when the artist forgets to click Save.
  const autoSaveAfterUpload = async (nextSections) => {
    try {
      await patchProject(slug, {
        ...form,
        canvas_on_landing: canvasOnLanding,
        allow_download: allowDownload,
        test_mode: testMode,
        sections: buildSectionsPayload(nextSections || sections),
        closing_block: buildClosingPayload(),
      });
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    }
  };

  const setSection = (key, field, value) =>
    setSections((prev) => prev.map((s) => {
      if (s.key !== key) return s;
      const mapKey = field === "label" ? "label_i18n" : "body_i18n";
      return { ...s, [mapKey]: { ...(s[mapKey] || {}), [secLang]: value } };
    }));

  const sectionHasContent = (s) =>
    !!((s.body_i18n?.en || s.body || "").trim()) || !!s.image || !!s.video;
  const sectionStatus = (s) =>
    s.active === false ? "HIDDEN" : (sectionHasContent(s) ? "LIVE" : "EMPTY");
  const setSectionActive = (key, v) =>
    setSections((prev) => prev.map((x) => (x.key === key ? { ...x, active: v } : x)));
  const removeImg = async (key) => {
    try {
      await removeSectionImage(slug, key);
      setSections((prev) => prev.map((x) => (x.key === key ? { ...x, image: null } : x)));
      toast.success("Image removed");
    } catch (e) { toast.error("Could not remove image"); }
  };
  const removeVid = async (key) => {
    try {
      await removeSectionVideo(slug, key);
      setSections((prev) => prev.map((x) => (x.key === key ? { ...x, video: null } : x)));
      toast.success("Video removed");
    } catch (e) { toast.error("Could not remove video"); }
  };
  const setSectionMediaType = (key, v) =>
    setSections((prev) => prev.map((x) => (x.key === key ? { ...x, media_type: v } : x)));
  const removeClosingImg = async () => {
    try {
      await removeClosingImage(slug);
      setClosing((c) => ({ ...c, image: null }));
      toast.success("Image removed");
    } catch (e) { toast.error("Could not remove image"); }
  };

  const runTranslate = async (regen) => {    if (translating) return;
    setTranslating(true);
    try {
      await saveTexts();                       // persist English master + any manual edits first
      const r = await translateSections(slug, regen);
      setSections((r.sections || []).map((s) => ({
        ...s,
        label_i18n: s.label_i18n || { en: s.label || "" },
        body_i18n: s.body_i18n || { en: s.body || "" },
      })));
      toast.success(regen ? `${regen.toUpperCase()} translation regenerated` : "Translations generated");
    } catch (e) { toast.error("Translation failed. Try again."); }
    finally { setTranslating(false); }
  };

  const setTrigField = (k, v) => setTrig((t) => ({ ...t, [k]: v }));
  const editingDefault = !trigCp && !trigPlatform;
  const loadOverride = (o) => {
    setTrigCp(o.content_piece || ""); setTrigPlatform(o.platform || "");
    setTrig({ ...EMPTY_TRIGGER, ...(project.trigger_default || {}), ...o });
  };
  const newOverride = () => {
    setTrigCp(""); setTrigPlatform("");
    setTrig({ ...EMPTY_TRIGGER, ...(project.trigger_default || {}) });
  };
  const saveTrigger = async () => {
    if (!editingDefault && !trigCp.trim() && !trigPlatform.trim()) {
      toast.error("An override needs a content piece and/or platform"); return;
    }
    setTrigSaving(true);
    try {
      if (editingDefault) { await setTriggerDefault(slug, trig); toast.success("Default trigger saved"); }
      else { await setTriggerOverride(slug, trigCp.trim(), trigPlatform.trim(), trig); toast.success("Override saved"); }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed"); }
    finally { setTrigSaving(false); }
  };
  const removeOverride = async (o) => {
    try { await deleteTriggerOverride(slug, o.content_piece || "", o.platform || ""); toast.success("Override removed"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };
  const openExport = () =>
    window.open(`/trigger/${slug}?content_piece=${encodeURIComponent(trigCp)}&platform=${encodeURIComponent(trigPlatform)}&bg=chroma`, "_blank");

  const linkSrc = () => {
    const p = trigPlatform || "instagram";
    if (trig.trigger_type === "story_link") return `${p}_story`;
    if (trig.trigger_type === "profile_link") return `${p}_profile`;
    if (trig.trigger_type === "direct_link") return trigPlatform || "direct";
    return trigPlatform || "";
  };
  const generatedUrl = () => {
    if (!trig.trigger_id) return "";
    const p = new URLSearchParams();
    const src = linkSrc(); if (src) p.set("src", src);
    if (trigCp) p.set("content", trigCp);
    p.set("trigger", trig.trigger_id);
    // /api/share = OpenGraph-rich link that fires the FANLY web player
    return `${BACKEND}/api/share/${slug}?${p.toString()}`;
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(generatedUrl()); toast.success("Link copied"); }
    catch (e) { toast.error("Copy failed"); }
  };
  const qrParams = () => {
    const src = linkSrc();
    return { src: src ? `${src}_qr` : "qr", content: trigCp || "", trigger: trig.trigger_id || "" };
  };
  const downloadQr = async (fmt) => {
    try {
      const blob = await fetchQrBlob(slug, qrParams(), fmt);
      triggerDownload(blob, `fanly-${slug}-qr.${fmt}`);
      toast.success(`QR ${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error("QR download failed"); }
  };
  const previewQr = async () => {
    try {
      const blob = await fetchQrBlob(slug, qrParams(), "png");
      setQrPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
    } catch (e) { toast.error("QR preview failed"); }
  };
  const isLinkType = LINK_TYPES.includes(trig.trigger_type);
  const stat = trigStats[trig.trigger_id] || { visits: 0, unlocks: 0, conversion_rate: 0 };

  if (!project) {
    return (
      <div className="min-h-[100dvh] bg-[#050505] flex items-center justify-center text-[#737373]">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const audioAcc = "audio/mpeg,audio/mp3,audio/wav,audio/aac,audio/mp4,audio/x-m4a,audio/ogg,audio/flac";
  const imgAcc = "image/png,image/jpeg,image/jpg,image/webp,image/heic,image/heif,.png,.jpg,.jpeg,.webp,.heic,.heif";
  const videoAcc = "video/mp4,video/webm,video/quicktime";
  const canvasVideo = project.branding?.canvas_video;

  return (
    <div data-testid="admin-editor">
      <div className="max-w-3xl">
        <Tabs value={tab}>

          {/* RELEASE */}
          <TabsContent value="release" className="pt-6 space-y-6">
            {[
              ["name", "Release name"],
              ["artist", "Artist"],
              ["tagline", "Tagline"],
              ["release_label", "Release label"],
            ].map(([key, label]) => (
              <div key={key}>
                <label className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">{label}</label>
                <input
                  data-testid={`field-${key}`}
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#FF2A2A] outline-none py-2 text-lg text-white transition-colors"
                />
              </div>
            ))}
            <UploadCard
              testid="upload-cover" title="Cover / cinematic visual" kind="cover" slug={slug}
              accept={imgAcc} hint="Screen 1 full-screen background (portrait works best on mobile)."
              current={null}
              onDone={(res) => { if (res?.url) setProject((p) => ({ ...p, branding: { ...(p.branding || {}), cover_image: res.url } })); }}
            />

            {/* Canvas / Reel — Project asset, Spotify-Canvas-style loop */}
            <div className="border border-white/10 rounded-2xl p-5 space-y-4" data-testid="canvas-block">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Film className="w-4 h-4 text-[#FF2A2A]" />
                  <h4 className="font-semibold">Canvas / Reel</h4>
                </div>
                <p className="font-mono-x text-[11px] text-[#737373] leading-relaxed">
                  Short vertical 9:16 loop (MP4, 3–10s) that plays muted behind the full-track player.
                  Cover art is used as a fallback while it loads or if none is set.
                </p>
              </div>
              {canvasVideo && (
                <video
                  data-testid="canvas-preview"
                  src={canvasVideo}
                  className="w-32 h-56 object-cover rounded-lg border border-white/10 bg-black"
                  muted loop autoPlay playsInline
                />
              )}
              <UploadCard
                testid="upload-canvas" title="Canvas video" kind="canvas" slug={slug}
                accept={videoAcc} hint="Upload a vertical MP4 loop." current={null}
                onDone={(res) => { if (res?.url) setProject((p) => ({ ...p, branding: { ...(p.branding || {}), canvas_video: res.url } })); }}
              />
              <label className="flex items-center justify-between gap-4 pt-2 border-t border-white/10">
                <span className="font-mono-x text-[11px] text-[#a3a3a3] leading-relaxed">
                  Show Canvas on Landing (Screen 1) — off for now, flip on when you're ready
                </span>
                <Switch
                  data-testid="canvas-on-landing"
                  checked={canvasOnLanding}
                  onCheckedChange={setCanvasOnLanding}
                />
              </label>
              <label className="flex items-center justify-between gap-4 pt-2 border-t border-white/10">
                <span className="font-mono-x text-[11px] text-[#a3a3a3] leading-relaxed">
                  Allow Download — let unlocked fans download the full track (secondary action, only after Unlock)
                </span>
                <Switch
                  data-testid="allow-download"
                  checked={allowDownload}
                  onCheckedChange={setAllowDownload}
                />
              </label>
              <div className="pt-3 border-t border-white/10 rounded-xl p-3" style={{ background: testMode ? "#FF6B6B14" : "transparent" }}>
                <label className="flex items-center justify-between gap-4">
                  <span className="font-mono-x text-[11px] leading-relaxed" style={{ color: testMode ? "#FF6B6B" : "#a3a3a3" }}>
                    TEST MODE — rehearse the full journey with your own email. Fans & events created while ON are marked as test and are EXCLUDED from Analytics, CRM and Broadcasts. Turn OFF before launch.
                  </span>
                  <Switch data-testid="test-mode" checked={testMode} onCheckedChange={setTestMode} />
                </label>
                <button data-testid="reset-test-data" onClick={doResetTestData}
                  className="mt-3 font-mono-x text-[10px] tracking-widest uppercase px-3 py-2 rounded-full border transition-colors"
                  style={{ color: "#FF6B6B", borderColor: "#FF6B6B55" }}>
                  Reset test data
                </button>
              </div>
            </div>

            <button data-testid="save-release" onClick={saveTexts} disabled={saving} className="flex items-center gap-2 bg-white text-black font-semibold rounded-full px-6 py-3 hover:scale-[1.02] transition-transform disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save release
            </button>

            {/* CUSTOM DOMAIN */}
            <div className="border rounded-2xl p-5 space-y-4" style={{ borderColor: "#5B6CFF44", background: "#5B6CFF0a" }} data-testid="custom-domain-block">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4" style={{ color: "#5B6CFF" }} />
                <div>
                  <p className="font-mono-x text-[11px] tracking-widest uppercase" style={{ color: "#5B6CFF" }}>Custom Domain · Artist-wide</p>
                  <p className="text-sm text-[#a3a3a3] mt-1">White-label the fan experience on your own subdomain, e.g. <span className="text-white">listen.yourdomain.com</span>. This is an <span className="text-white">artist-level</span> setting — connect it once and it serves <span className="text-white">all</span> of this artist's current and future releases and Smart Links (e.g. <span className="text-white">/another</span>, <span className="text-white">/t/19A86B</span>).</p>
                </div>
              </div>

              {(() => {
                const status = domain?.verification_status || "not_configured";
                const StatusPill = ({ color, label }) => (
                  <span data-testid="domain-status" className="font-mono-x text-[10px] tracking-widest uppercase px-2 py-1 rounded-full" style={{ color, border: `1px solid ${color}55`, background: `${color}14` }}>{label}</span>
                );
                const DnsRow = ({ type, name, value, tid }) => (
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 px-3 py-2 bg-black/30">
                    <div className="min-w-0">
                      <p className="font-mono-x text-[9px] tracking-widest uppercase text-[#737373]">{type} · {name}</p>
                      <p data-testid={tid} className="font-mono-x text-xs text-white break-all">{value}</p>
                    </div>
                    <button onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }}
                      className="shrink-0 font-mono-x text-[10px] uppercase px-3 py-1.5 rounded-full border border-white/15 text-white hover:border-white transition-colors"><Copy className="w-3 h-3" /></button>
                  </div>
                );
                return (
                  <>
                    {status === "verified" ? (
                      <div className="space-y-3" data-testid="domain-verified">
                        <div className="flex items-center gap-3">
                          <StatusPill color="#55E6B3" label="Verified · HTTPS active" />
                        </div>
                        <p className="font-mono-x text-sm break-all" style={{ color: "#55E6B3" }} data-testid="domain-live-url">https://{domain.domain}</p>
                        <p className="text-[11px] text-[#737373]">HTTPS is served by the platform edge for this host. Smart Links now resolve as <span className="text-white">https://{domain.domain}/t/&lt;code&gt;</span>.</p>
                        <button data-testid="domain-disconnect" onClick={doDisconnectDomain} disabled={domainBusy}
                          className="font-mono-x text-[10px] tracking-widest uppercase px-4 py-2 rounded-full border transition-colors disabled:opacity-60" style={{ color: "#FF6B6B", borderColor: "#FF6B6B55" }}>
                          {domainBusy ? "…" : "Disconnect domain"}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                          <label className="flex-1 flex flex-col gap-1">
                            <span className="font-mono-x text-[10px] uppercase text-[#737373]">Domain</span>
                            <input data-testid="domain-input" value={domainInput} onChange={(e) => setDomainInput(e.target.value)} placeholder="listen.yourdomain.com"
                              className="bg-transparent border border-white/15 rounded-lg px-3 py-2 text-white outline-none focus:border-[#5B6CFF]" />
                          </label>
                          <button data-testid="domain-connect" onClick={doConnectDomain} disabled={domainBusy}
                            className="inline-flex items-center justify-center gap-2 bg-white text-black font-semibold rounded-full px-5 py-2.5 text-sm hover:scale-[1.02] transition-transform disabled:opacity-60">
                            {domainBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} {domain?.domain ? "Update" : "Connect"}
                          </button>
                        </div>

                        {(status === "dns_pending" || status === "error") && domain?.cname_target && (
                          <div className="space-y-3" data-testid="domain-dns-instructions">
                            <div className="flex items-center gap-2">
                              {status === "error"
                                ? <StatusPill color="#FF6B6B" label="Not verified yet" />
                                : <StatusPill color="#FFB020" label="DNS pending" />}
                            </div>
                            <p className="text-[11px] text-[#a3a3a3]">Add these two records at your DNS provider, then click Verify. DNS can take a few minutes to propagate.</p>
                            <DnsRow type="CNAME" name={domain.cname_host} value={domain.cname_target} tid="domain-cname" />
                            <DnsRow type="TXT" name={domain.domain} value={domain.verification_token} tid="domain-txt" />
                            <button data-testid="domain-verify" onClick={doVerifyDomain} disabled={domainBusy}
                              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-transform hover:scale-[1.02] disabled:opacity-60" style={{ background: "#5B6CFF", color: "#fff" }}>
                              {domainBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Verify domain
                            </button>
                            <button data-testid="domain-disconnect" onClick={doDisconnectDomain} disabled={domainBusy}
                              className="ml-2 font-mono-x text-[10px] tracking-widest uppercase px-4 py-2 rounded-full border transition-colors disabled:opacity-60" style={{ color: "#FF6B6B", borderColor: "#FF6B6B55" }}>
                              {domainBusy ? "…" : "Disconnect"}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </TabsContent>

          {/* AUDIO */}
          <TabsContent value="audio" className="pt-6 space-y-5">
            <UploadCard
              testid="upload-preview" title="Preview clip (20–30s)" kind="preview" slug={slug} isAudio
              accept={audioAcc} hint="The pre-cut taste. Hard-stops at the clip's end."
              current={project.assets?.preview ? !project.assets.preview.is_placeholder : null} onDone={load}
            />
            <UploadCard
              testid="upload-full" title="Full master" kind="full" slug={slug} isAudio
              accept={audioAcc} hint="The complete track. Only unlocked fans can stream it."
              current={project.assets?.full ? !project.assets.full.is_placeholder : null} onDone={load}
            />
            <div className="rounded-2xl border border-white/10 p-5 space-y-4" data-testid="preview-window">
              <div>
                <p className="font-mono-x text-[11px] tracking-widest uppercase text-white">Default Preview</p>
                <p className="font-mono-x text-[11px] text-[#737373] mt-1">The taste every fan hears before unlocking. Drag to find the moment — no timestamps to convert. Reusable across Smart Links.</p>
              </div>
              {(() => {
                const st = Number(previewStart) || 0;
                const dur = Math.max(1, Math.round((Number(previewEnd) || 0) - st));
                const maxStart = Math.max(1, (trackDur || 240) - 1);
                const setStart = (v) => { const s = Math.max(0, Math.min(maxStart, Math.round(v))); setPreviewStart(s); setPreviewEnd(s + dur); };
                const setDur = (v) => { const d = Math.max(5, Math.min(40, Math.round(v))); setPreviewEnd(st + d); };
                return (
                  <>
                    <div className="space-y-2">
                      <input data-testid="preview-slider" type="range" min={0} max={maxStart} step={1} value={st}
                        onChange={(e) => setStart(Number(e.target.value))}
                        className="w-full accent-[#55E6B3]" style={{ accentColor: "#55E6B3" }} />
                      <div className="flex justify-between font-mono-x text-[9px] text-[#555]"><span>00:00</span><span>{secToMmss(trackDur || 240)}</span></div>
                    </div>
                    <div className="flex items-end gap-4 flex-wrap">
                      <label className="flex flex-col gap-1">
                        <span className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Start (mm:ss)</span>
                        <input data-testid="preview-start" value={startDraft ?? secToMmss(st)} placeholder="00:20" inputMode="numeric"
                        onFocus={(e) => { setStartDraft(secToMmss(st)); e.target.select(); }}
                        onChange={(e) => setStartDraft(e.target.value)}
                        onBlur={() => { if (startDraft !== null) setStart(mmssToSec(startDraft)); setStartDraft(null); }}
                        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                          className="w-24 bg-transparent border border-white/15 rounded-lg px-3 py-2 text-white outline-none focus:border-[#55E6B3]" />
                      </label>
                      <label className="flex flex-col gap-1">
                        <span className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Duration (sec)</span>
                        <input data-testid="preview-duration-input" type="number" min={5} max={40} step={1} value={dur} onChange={(e) => setDur(Number(e.target.value))}
                          className="w-24 bg-transparent border border-white/15 rounded-lg px-3 py-2 text-white outline-none focus:border-[#55E6B3]" />
                      </label>
                      <div className="font-mono-x text-[10px] text-[#737373]">End<br /><span className="text-white text-sm">{secToMmss(st + dur)}</span></div>
                    </div>
                    <p className="font-mono-x text-[11px]" data-testid="preview-hear" style={{ color: "#55E6B3" }}>
                      Listeners will hear: {secToMmss(st)} → {secToMmss(st + dur)}
                    </p>
                  </>
                );
              })()}
              <div className="flex items-center gap-3">
                <button data-testid="preview-play" onClick={playPreviewTest}
                  className="inline-flex items-center gap-2 font-mono-x text-[11px] tracking-widest uppercase px-4 py-2 rounded-full border border-white/15 text-white hover:border-[#55E6B3] transition-colors">
                  <Music className="w-3.5 h-3.5" /> Preview this section
                </button>
                <button data-testid="preview-save" onClick={savePreviewWindow} disabled={savingPreview}
                  className="inline-flex items-center gap-2 bg-white text-black font-semibold rounded-full px-5 py-2 text-sm hover:scale-[1.02] transition-transform disabled:opacity-60">
                  {savingPreview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Default Preview
                </button>
                <audio ref={previewTestRef} className="hidden" />
              </div>
            </div>
          </TabsContent>

          {/* SECTIONS */}
          <TabsContent value="sections" className="pt-6 space-y-8">
            <div className="flex items-center gap-2 flex-wrap justify-between" data-testid="section-lang-tabs">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373] mr-1">Editing language</span>
                {SEC_LANGS.map((l) => (
                  <button key={l} data-testid={`section-lang-${l}`} onClick={() => setSecLang(l)}
                    className={`px-3 py-1.5 rounded-full text-xs font-mono-x tracking-widest uppercase border transition-colors ${secLang === l ? "bg-[#FF2A2A] border-[#FF2A2A] text-white" : "border-white/15 text-[#8a8a8a] hover:text-white"}`}>
                    {SEC_LANG_LABELS[l]}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                {secLang !== "en" && (
                  <button data-testid="regenerate-translation" onClick={() => runTranslate(secLang)} disabled={translating}
                    className="flex items-center gap-2 border border-white/20 rounded-full px-4 py-2 text-xs hover:border-[#FF2A2A] transition-colors disabled:opacity-60">
                    {translating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Languages className="w-3.5 h-3.5" />}
                    Regenerate {SEC_LANG_LABELS[secLang]}
                  </button>
                )}
                <button data-testid="generate-translations" onClick={() => runTranslate(null)} disabled={translating}
                  className="flex items-center gap-2 bg-[#FF2A2A] text-white rounded-full px-4 py-2 text-xs font-semibold hover:scale-[1.02] transition-transform disabled:opacity-60">
                  {translating ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Translating…</> : <><Languages className="w-3.5 h-3.5" /> Generate translations</>}
                </button>
              </div>
            </div>
            {secLang !== "en" && (
              <p className="font-mono-x text-[10px] text-[#8a8a8a] -mt-4">EN is the master. Empty fields fall back to English on the fan page. Generate fills only empty fields; Regenerate replaces this language.</p>
            )}
            {sections.map((s) => (
              <div key={s.key} className="border border-white/10 rounded-2xl p-5 space-y-4" data-testid={`section-edit-${s.key}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <span className="font-display font-bold tracking-tight">{s.label_i18n?.en || s.label || s.key}</span>
                    {(() => {
                      const st = sectionStatus(s);
                      const cls = st === "LIVE"
                        ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
                        : st === "HIDDEN"
                        ? "text-[#8a8a8a] border-white/20 bg-white/5"
                        : "text-amber-400 border-amber-500/40 bg-amber-500/10";
                      return (
                        <span data-testid={`section-status-${s.key}`}
                          className={`font-mono-x text-[9px] tracking-widest uppercase border rounded-full px-2 py-0.5 ${cls}`}>
                          {st}
                        </span>
                      );
                    })()}
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Session active</span>
                    <Switch data-testid={`section-active-${s.key}`} checked={s.active !== false}
                      onCheckedChange={(v) => setSectionActive(s.key, v)} />
                  </label>
                </div>
                {s.active !== false && !sectionHasContent(s) && (
                  <p data-testid={`section-empty-warning-${s.key}`} className="font-mono-x text-[11px] text-amber-400 leading-relaxed">
                    This session has no publishable content and will remain hidden.
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <span className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Display type</span>
                  <select data-testid={`section-display-${s.key}`} value={s.display_type || "standard"}
                    onChange={(e) => setSections((prev) => prev.map((x) => x.key === s.key ? { ...x, display_type: e.target.value } : x))}
                    className="bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-1.5 text-sm text-white">
                    <option value="standard">Standard</option>
                    <option value="lyrics">Lyrics</option>
                  </select>
                  {s.display_type === "lyrics" && <span className="font-mono-x text-[10px] text-[#8a8a8a]">Editorial lyrics layout · not auto-translated</span>}
                </div>
                {s.display_type !== "lyrics" && (
                  <div className="flex items-center gap-2" data-testid={`section-media-type-row-${s.key}`}>
                    <span className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Media type</span>
                    <select data-testid={`section-media-type-${s.key}`} value={s.media_type || "image"}
                      onChange={(e) => setSectionMediaType(s.key, e.target.value)}
                      className="bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-1.5 text-sm text-white">
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                    </select>
                    <span className="font-mono-x text-[10px] text-[#8a8a8a]">{s.media_type === "video" ? "16:9 · 1920×1080" : "21:9 cinematic banner"}</span>
                  </div>
                )}
                <div>
                  <label className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373] flex items-center gap-2">
                    Section title · {SEC_LANG_LABELS[secLang]}
                    {secLang !== "en" && (
                      (s.label_i18n?.[secLang])
                        ? <span data-testid={`badge-label-${s.key}`} className="inline-flex items-center gap-1 text-emerald-400 normal-case tracking-normal"><Check className="w-3 h-3" />translated</span>
                        : <span data-testid={`badge-label-${s.key}`} className="inline-flex items-center gap-1 text-amber-400 normal-case tracking-normal"><CornerDownRight className="w-3 h-3" />English fallback</span>
                    )}
                  </label>
                  <input
                    data-testid={`section-label-${s.key}`}
                    value={s.label_i18n?.[secLang] ?? ""}
                    placeholder={secLang !== "en" ? (s.label_i18n?.en || "English fallback") : ""}
                    onChange={(e) => setSection(s.key, "label", e.target.value)}
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#FF2A2A] outline-none py-2 text-white transition-colors"
                  />
                </div>
                <div>
                  <label className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373] flex items-center gap-2">
                    Body · {SEC_LANG_LABELS[secLang]}
                    {secLang !== "en" && (
                      (s.body_i18n?.[secLang])
                        ? <span data-testid={`badge-body-${s.key}`} className="inline-flex items-center gap-1 text-emerald-400 normal-case tracking-normal"><Check className="w-3 h-3" />translated</span>
                        : <span data-testid={`badge-body-${s.key}`} className="inline-flex items-center gap-1 text-amber-400 normal-case tracking-normal"><CornerDownRight className="w-3 h-3" />English fallback</span>
                    )}
                  </label>
                  <textarea
                    data-testid={`section-body-${s.key}`}
                    value={s.body_i18n?.[secLang] ?? ""}
                    placeholder={secLang !== "en" ? (s.body_i18n?.en || "English fallback") : ""}
                    onChange={(e) => setSection(s.key, "body", e.target.value)}
                    rows={4}
                    className="w-full bg-transparent border border-white/15 rounded-lg focus:border-[#FF2A2A] outline-none p-3 text-[#e5e5e5] transition-colors resize-y"
                  />
                </div>
                {s.display_type !== "lyrics" && s.media_type === "video" ? (
                  <SectionVideoCard slug={slug} sectionKey={s.key} video={s.video}
                    onUploaded={(url) => setSections((prev) => prev.map((x) => x.key === s.key ? { ...x, video: url } : x))}
                    onRemove={() => removeVid(s.key)} />
                ) : (
                  <>
                    {s.image && (
                      <div className="relative w-full overflow-hidden rounded-xl border border-white/10" style={{ background: "#151923" }} data-testid={`section-image-frame-${s.key}`}>
                        <img src={s.image} alt="" className="w-full h-auto block" />
                        <button data-testid={`remove-section-image-${s.key}`} onClick={() => removeImg(s.key)}
                          className="absolute top-2 right-2 z-10 flex items-center gap-1.5 bg-black/70 backdrop-blur-sm text-white text-xs rounded-full px-3 py-1.5 hover:bg-[#FF2A2A] transition-colors">
                          <Trash2 className="w-3.5 h-3.5" /> Remove image
                        </button>
                      </div>
                    )}
                    <UploadCard
                      testid={`upload-section-${s.key}`} title="Section image" kind={`section_${s.key}`} slug={slug}
                      accept={imgAcc} hint="Recommended image ratio: 21:9 — shown as a cinematic banner." current={null}
                      onDone={(res) => {
                        if (!res?.url) return;
                        setSections((prev) => {
                          const next = prev.map((x) => x.key === s.key ? { ...x, image: res.url } : x);
                          autoSaveAfterUpload(next);
                          return next;
                        });
                      }}
                    />
                  </>
                )}
              </div>
            ))}
            {/* CLOSING / WHAT'S NEXT — project-level retention block (NOT a session) */}
            <div className="border border-[#FF2A2A]/25 rounded-2xl p-5 space-y-4 bg-[#160707]/30" data-testid="closing-block-editor">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="font-display font-bold tracking-tight">Closing / What's Next</span>
                  <span className="font-mono-x text-[9px] tracking-widest uppercase border rounded-full px-2 py-0.5 text-[#FF2A2A] border-[#FF2A2A]/40 bg-[#FF2A2A]/10">Retention block</span>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Closing block active</span>
                  <Switch data-testid="closing-active" checked={closing.active !== false}
                    onCheckedChange={(v) => setClosing((c) => ({ ...c, active: v }))} />
                </label>
              </div>
              <p className="font-mono-x text-[10px] text-[#8a8a8a] leading-relaxed">Shown once at the very bottom of the fan page, after all sessions. It never counts toward session progress or completion.</p>
              <div>
                <label className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Title</label>
                <input data-testid="closing-title-input" value={closing.title}
                  onChange={(e) => setClosing((c) => ({ ...c, title: e.target.value }))}
                  className="mt-1 w-full bg-[#0e0e0e] border border-white/15 rounded-lg px-3 py-2 text-sm text-white" />
              </div>
              <div>
                <label className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Body</label>
                <textarea data-testid="closing-body-input" value={closing.body} rows={3}
                  onChange={(e) => setClosing((c) => ({ ...c, body: e.target.value }))}
                  className="mt-1 w-full bg-[#0e0e0e] border border-white/15 rounded-lg px-3 py-2 text-sm text-white leading-relaxed" />
              </div>
              <div className="flex items-end gap-3 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <label className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">CTA label</label>
                  <input data-testid="closing-cta-input" value={closing.cta_label}
                    onChange={(e) => setClosing((c) => ({ ...c, cta_label: e.target.value }))}
                    className="mt-1 w-full bg-[#0e0e0e] border border-white/15 rounded-lg px-3 py-2 text-sm text-white" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer pb-2">
                  <span className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">CTA enabled</span>
                  <Switch data-testid="closing-cta-enabled" checked={closing.cta_enabled !== false}
                    onCheckedChange={(v) => setClosing((c) => ({ ...c, cta_enabled: v }))} />
                </label>
              </div>
              {closing.image && (
                <div className="relative w-full overflow-hidden rounded-xl border border-white/10" style={{ aspectRatio: "21 / 9" }} data-testid="closing-image-frame">
                  <img src={closing.image} alt="" className="absolute inset-0 w-full h-full object-cover object-center block" />
                  <button data-testid="remove-closing-image" onClick={removeClosingImg}
                    className="absolute top-2 right-2 z-10 flex items-center gap-1.5 bg-black/70 backdrop-blur-sm text-white text-xs rounded-full px-3 py-1.5 hover:bg-[#FF2A2A] transition-colors">
                    <Trash2 className="w-3.5 h-3.5" /> Remove image
                  </button>
                </div>
              )}
              <UploadCard
                testid="upload-closing-image" title="Closing image (optional)" kind="closing_image" slug={slug}
                accept={imgAcc} hint="Optional — recommended 21:9. Leave empty for a clean text-only block." current={null}
                onDone={(res) => {
                  if (!res?.url) return;
                  setClosing((c) => ({ ...c, image: res.url }));
                  autoSaveAfterUpload();
                }}
              />
            </div>

            <button data-testid="save-sections" onClick={saveTexts} disabled={saving} className="flex items-center gap-2 bg-white text-black font-semibold rounded-full px-6 py-3 hover:scale-[1.02] transition-transform disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save sections
            </button>
          </TabsContent>

          {/* TRIGGER */}
          <TabsContent value="trigger" className="pt-6" data-testid="tab-trigger-content">
            <div className="rounded-2xl border p-5 mb-8 space-y-5" style={{ borderColor: "#5B6CFF44", background: "#5B6CFF0a" }} data-testid="smart-content">
              <div>
                <p className="font-mono-x text-[11px] tracking-widest uppercase" style={{ color: "#5B6CFF" }}>Smart Content</p>
                <p className="text-sm text-[#a3a3a3] mt-1">Turn a finished Reel into an attributed trigger: Reel → Smart Link → ANOTHER Preview → Unlock.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="flex flex-col gap-1"><span className="font-mono-x text-[10px] uppercase text-[#737373]">Content piece</span>
                  <input data-testid="sc-content" value={sc.content_piece} onChange={(e)=>setSc({...sc,content_piece:e.target.value})}
                    className="bg-transparent border border-white/15 rounded-lg px-3 py-2 text-white outline-none focus:border-[#5B6CFF]" /></label>
                <label className="flex flex-col gap-1"><span className="font-mono-x text-[10px] uppercase text-[#737373]">Platform</span>
                  <select data-testid="sc-platform" value={sc.platform} onChange={(e)=>setSc({...sc,platform:e.target.value})}
                    className="bg-[#151923] border border-white/15 rounded-lg px-3 py-2 text-white outline-none focus:border-[#5B6CFF]">
                    <option value="instagram">Instagram</option><option value="tiktok">TikTok</option><option value="youtube">YouTube</option></select></label>
                <label className="flex flex-col gap-1"><span className="font-mono-x text-[10px] uppercase text-[#737373]">Trigger type</span>
                  <select data-testid="sc-trigger" value={sc.trigger_type} onChange={(e)=>{setSc({...sc,trigger_type:e.target.value}); setSmartLink(null);}}
                    className="bg-[#151923] border border-white/15 rounded-lg px-3 py-2 text-white outline-none focus:border-[#5B6CFF]">
                    <option value="profile_link">Profile Link</option><option value="story_link">Story Link</option>
                    <option value="direct_link">Direct Link</option><option value="comment_keyword">Comment Keyword</option>
                    <option value="dm_keyword">DM Keyword</option></select></label>
                <label className="flex flex-col gap-1"><span className="font-mono-x text-[10px] uppercase text-[#737373]">Trigger target</span>
                  <input readOnly value="ANOTHER — Preview" className="bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white/70" /></label>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="flex flex-col gap-1"><span className="font-mono-x text-[10px] uppercase text-[#737373]">Hook / Smart message</span>
                  <input data-testid="sc-hook" value={sc.hook} onChange={(e)=>setSc({...sc,hook:e.target.value})}
                    className="bg-transparent border border-white/15 rounded-lg px-3 py-2 text-white outline-none focus:border-[#5B6CFF]" /></label>
                <label className="flex flex-col gap-1"><span className="font-mono-x text-[10px] uppercase text-[#737373]">Action</span>
                  <input data-testid="sc-action" value={sc.action} onChange={(e)=>setSc({...sc,action:e.target.value})}
                    className="bg-transparent border border-white/15 rounded-lg px-3 py-2 text-white outline-none focus:border-[#5B6CFF]" /></label>
              </div>

              {/* WHAT SHOULD HAPPEN WHEN THE FAN OPENS THIS LINK? */}
              <div className="space-y-2" data-testid="sc-audio-mode">
                <p className="font-mono-x text-[11px] tracking-widest uppercase text-white">What should happen when the fan opens this link?</p>
                {[["saved","Play my saved preview","Use the Default Preview configured for this song.",false],
                  ["moment","Start the song at a specific moment","Pick any moment of the master — this Smart Link only, your Default Preview stays untouched.",false],
                  ["video","Continue my social video","Continue the song from the musical point where the social video ended.",true]].map(([val,title,desc,beta])=>(
                  <button key={val} type="button" data-testid={`sc-mode-${val}`} onClick={()=>{setAudioMode(val); setSmartLink(null);}}
                    className="w-full text-left rounded-xl border px-4 py-3 transition-colors"
                    style={{borderColor: audioMode===val ? "#5B6CFF" : "rgba(255,255,255,0.12)", background: audioMode===val ? "#5B6CFF14" : "transparent"}}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono-x text-[11px] tracking-widest uppercase text-white">{title}</span>
                      {beta && <span className="font-mono-x text-[8px] tracking-widest uppercase px-1.5 py-0.5 rounded" style={{color:"#FFB020",border:"1px solid #FFB02055"}}>BETA</span>}
                      {val==="saved" && <span className="font-mono-x text-[8px] tracking-widest uppercase px-1.5 py-0.5 rounded" style={{color:"#55E6B3",border:"1px solid #55E6B355"}}>Recommended</span>}
                    </div>
                    <p className="text-[12px] text-[#a3a3a3] mt-1">{desc}</p>
                  </button>
                ))}
              </div>

              {/* only-relevant fields */}
              {audioMode !== "saved" && (
                <div className="grid sm:grid-cols-2 gap-3" data-testid="sc-moment-fields">
                  <label className="flex flex-col gap-1"><span className="font-mono-x text-[10px] uppercase text-[#737373]">{audioMode==="video" ? "Continue song from (mm:ss)" : "Start at (mm:ss)"}</span>
                    <input data-testid="sc-continue-from" value={sc.continue_from} onChange={(e)=>{setSc({...sc,continue_from:e.target.value}); setSmartLink(null);}} placeholder="01:01"
                      className="bg-transparent border border-white/15 rounded-lg px-3 py-2 text-white outline-none focus:border-[#5B6CFF]" /></label>
                  <label className="flex flex-col gap-1"><span className="font-mono-x text-[10px] uppercase text-[#737373]">Duration (sec)</span>
                    <input data-testid="sc-duration" type="number" min={5} max={60} step={1} value={scDur} onChange={(e)=>{setScDur(Number(e.target.value)); setSmartLink(null);}}
                      className="bg-transparent border border-white/15 rounded-lg px-3 py-2 text-white outline-none focus:border-[#5B6CFF]" /></label>
                  <p className="sm:col-span-2 font-mono-x text-[11px]" style={{color:"#55E6B3"}}>Fans will hear the master from {secToMmss(mmssToSec(sc.continue_from))} → {secToMmss(mmssToSec(sc.continue_from)+Number(scDur||25))}</p>
                </div>
              )}

              {/* WHEN SHOULD THE CTA APPEAR? */}
              <div className="space-y-2" data-testid="sc-cta-timing">
                <p className="font-mono-x text-[11px] tracking-widest uppercase text-white">When should the CTA appear?</p>
                <div className="flex flex-wrap gap-2 items-center">
                  <button type="button" data-testid="cta-entire" onClick={()=>setCtaMode("entire")}
                    className="font-mono-x text-[11px] uppercase px-4 py-2 rounded-full border transition-colors"
                    style={{borderColor: ctaMode==="entire"?"#5B6CFF":"rgba(255,255,255,0.15)", color: ctaMode==="entire"?"#fff":"#a3a3a3"}}>During the entire preview</button>
                  <button type="button" data-testid="cta-last" onClick={()=>setCtaMode("last")}
                    className="font-mono-x text-[11px] uppercase px-4 py-2 rounded-full border transition-colors"
                    style={{borderColor: ctaMode==="last"?"#5B6CFF":"rgba(255,255,255,0.15)", color: ctaMode==="last"?"#fff":"#a3a3a3"}}>During the last</button>
                  {ctaMode==="last" && (
                    <input data-testid="cta-last-secs" type="number" min={2} max={20} step={1} value={ctaLast} onChange={(e)=>setCtaLast(Number(e.target.value))}
                      className="w-16 bg-transparent border border-white/15 rounded-lg px-2 py-1.5 text-white outline-none focus:border-[#5B6CFF]" />
                  )}
                  {ctaMode==="last" && <span className="font-mono-x text-[11px] text-[#737373]">seconds</span>}
                </div>
                <button type="button" onClick={()=>setShowAdvanced(!showAdvanced)} className="font-mono-x text-[10px] uppercase text-[#5B6CFF]">Advanced settings {showAdvanced?"▲":"▼"}</button>
                {showAdvanced && (
                  <div className="grid sm:grid-cols-2 gap-3 pt-1" data-testid="sc-advanced">
                    <label className="flex flex-col gap-1"><span className="font-mono-x text-[10px] uppercase text-[#737373]">CTA start (s, relative to preview)</span>
                      <input data-testid="sc-start" type="number" step="0.1" value={sc.cta_start} onChange={(e)=>setSc({...sc,cta_start:e.target.value})}
                        className="bg-transparent border border-white/15 rounded-lg px-3 py-2 text-white outline-none focus:border-[#5B6CFF]" /></label>
                    <label className="flex flex-col gap-1"><span className="font-mono-x text-[10px] uppercase text-[#737373]">CTA end (s, relative to preview)</span>
                      <input data-testid="sc-end" type="number" step="0.1" value={sc.cta_end} onChange={(e)=>setSc({...sc,cta_end:e.target.value})}
                        className="bg-transparent border border-white/15 rounded-lg px-3 py-2 text-white outline-none focus:border-[#5B6CFF]" /></label>
                  </div>
                )}
              </div>

              <a data-testid="sc-test-fan" href={fanTestUrl()} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 font-mono-x text-[11px] tracking-widest uppercase px-4 py-2 rounded-full border border-[#5B6CFF] text-white hover:bg-[#5B6CFF14] transition-colors">
                <Zap className="w-3.5 h-3.5" /> Test fan experience
              </a>
              {scAutomation ? (
                <div className="rounded-lg px-4 py-3 font-mono-x text-[11px] tracking-widest uppercase" style={{ color: "#FF6B6B", background: "#FF6B6B14", border: "1px solid #FF6B6B44" }} data-testid="automation-not-connected">
                  AUTOMATION NOT CONNECTED — connect a social automation to use keyword triggers.
                </div>
              ) : (
                <button data-testid="sc-generate" onClick={() => genSmartLink()} disabled={genBusy}
                  className="inline-flex items-center gap-2 bg-white text-black font-semibold rounded-full px-6 py-3 text-sm hover:scale-[1.02] transition-transform disabled:opacity-60">
                  {genBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Generate Smart Link
                </button>
              )}
              {smartLink && !scAutomation && (
                <div className="rounded-xl border border-white/10 p-4 space-y-3" data-testid="smart-link-result">
                  <div>
                    <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Smart Link</p>
                    <p data-testid="smart-link-url" className="font-mono-x text-sm text-[#55E6B3] break-all mt-1">{smartLink.url}</p>
                  </div>
                  <div className="flex gap-2">
                    <button data-testid="sc-copy" onClick={()=>{navigator.clipboard.writeText(smartLink.url); toast.success("Copied");}}
                      className="font-mono-x text-[11px] uppercase px-4 py-2 rounded-full border border-white/15 text-white hover:border-white transition-colors">Copy link</button>
                    <a data-testid="sc-open-test" href={smartLink.url} target="_blank" rel="noreferrer"
                      className="font-mono-x text-[11px] uppercase px-4 py-2 rounded-full border border-white/15 text-white hover:border-white transition-colors">Open test</a>
                  </div>
                  <div className="pt-3 border-t border-white/10 space-y-1 text-sm" data-testid="ready-to-use">
                    <p className="font-mono-x text-[10px] tracking-widest uppercase" style={{ color: "#55E6B3" }}>Ready to use</p>
                    <p className="text-[#a3a3a3]">Platform: <span className="text-white">{smartLink.platform || sc.platform}</span></p>
                    <p className="text-[#a3a3a3]">Content Piece: <span className="text-white">{smartLink.content_piece || sc.content_piece}</span></p>
                    <p className="text-[#a3a3a3]">Trigger: <span className="text-white">{smartLink.trigger_type || sc.trigger_type}</span></p>
                    <p className="text-[#a3a3a3]">Target: <span className="text-white">ANOTHER — Preview</span></p>
                    <p className="text-[#a3a3a3]">Preview behavior: <span className="text-white">{smartLink.preview_mode === "continue_from_reel" ? `Start at ${secToMmss(smartLink.continue_from_sec || 0)} · ${smartLink.duration_sec || 25}s` : `Saved preview · ${smartLink.duration_sec || 25}s`}</span></p>
                    <p className="text-[#a3a3a3]">Overlay: <span className="text-white">{sc.hook} · {sc.action}</span></p>
                    {sc.trigger_type === "profile_link" && (
                      <p className="text-[11px] mt-1" style={{ color: "#FFB020" }} data-testid="attribution-note">Attribution note: profile-link attribution is campaign-level, not exact-post.</p>
                    )}
                    <p className="text-white/80 mt-2">Place this Smart Link {scInstruction() ? `(${scInstruction()})` : ""} before publishing the Reel.</p>
                  </div>
                </div>
              )}
            </div>

            <p className="text-sm text-[#a3a3a3] leading-relaxed mb-6">
              A reusable cinematic overlay for Reels / TikTok / Shorts. Set a project default, then add
              per-<span className="text-white">content piece</span> / <span className="text-white">platform</span> overrides.
            </p>

            {/* scope selector */}
            <div className="border border-white/10 rounded-2xl p-5 mb-6">
              <div className="flex items-center justify-between mb-4">
                <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">
                  Editing: {editingDefault ? "Project default" : `Override · ${trigCp || "—"} / ${trigPlatform || "any"}`}
                </p>
                <button onClick={newOverride} data-testid="trigger-edit-default" className="font-mono-x text-[10px] tracking-widest uppercase text-[#FF2A2A] hover:text-white">
                  Edit default
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Content piece (override)</label>
                  <input data-testid="trig-content-piece" value={trigCp} onChange={(e) => setTrigCp(e.target.value)} placeholder="e.g. teaser-1"
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#FF2A2A] outline-none py-2 text-white transition-colors" />
                </div>
                <div>
                  <label className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Platform (override)</label>
                  <select data-testid="trig-platform" value={trigPlatform} onChange={(e) => setTrigPlatform(e.target.value)}
                    className="w-full bg-[#0e0e0e] border-b border-white/20 focus:border-[#FF2A2A] outline-none py-2 text-white">
                    {["", "instagram", "tiktok", "stories", "youtube"].map((p) => <option key={p} value={p}>{p || "— (default)"}</option>)}
                  </select>
                </div>
              </div>
              {trigOverrides.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {trigOverrides.map((o, i) => (
                    <span key={i} className="flex items-center gap-2 border border-white/15 rounded-full pl-3 pr-2 py-1 text-xs">
                      <button data-testid={`load-override-${i}`} onClick={() => loadOverride(o)} className="font-mono-x text-[11px] text-[#cfcfcf] hover:text-white">
                        {(o.content_piece || "—")}/{(o.platform || "any")}
                      </button>
                      <button onClick={() => removeOverride(o)} className="text-[#737373] hover:text-[#FF2A2A]"><Trash2 className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* fields */}
              <div className="space-y-5">
                {[["project_title", "Project title"], ["supporting_line", "Supporting line"], ["instruction_line", "Instruction line"], ["brand_signature", "Brand signature"]].map(([k, label]) => (
                  <div key={k}>
                    <label className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">{label}</label>
                    <input data-testid={`trig-${k}`} value={trig[k] || ""} onChange={(e) => setTrigField(k, e.target.value)}
                      className="w-full bg-transparent border-b border-white/20 focus:border-[#FF2A2A] outline-none py-2 text-white transition-colors" />
                  </div>
                ))}
                {[["trigger_type", "Trigger type", TRIGGER_TYPES], ["variant", "Variant", VARIANTS], ["tone_preset", "Tone preset", TONES]].map(([k, label, opts]) => (
                  <div key={k}>
                    <label className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">{label}</label>
                    <select data-testid={`trig-${k}`} value={trig[k]} onChange={(e) => setTrigField(k, e.target.value)}
                      className="w-full bg-[#0e0e0e] border-b border-white/20 focus:border-[#FF2A2A] outline-none py-2 text-white">
                      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                ))}
                <div className="flex flex-wrap gap-3 pt-2">
                  <button data-testid="save-trigger" onClick={saveTrigger} disabled={trigSaving} className="flex items-center gap-2 bg-white text-black font-semibold rounded-full px-6 py-3 hover:scale-[1.02] transition-transform disabled:opacity-60">
                    {trigSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {editingDefault ? "Save default" : "Save override"}
                  </button>
                  <button data-testid="open-export" onClick={openExport} className="flex items-center gap-2 border border-white/20 rounded-full px-5 py-3 text-sm hover:border-[#FF2A2A] transition-colors">
                    <ExternalLink className="w-4 h-4" /> Export view
                  </button>
                </div>
              </div>

              {/* live preview */}
              <div>
                <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373] mb-3 flex items-center gap-2">
                  <Zap className="w-3.5 h-3.5 text-[#FF2A2A]" /> Live preview
                </p>
                <div className="relative mx-auto overflow-hidden rounded-xl border border-white/10 bg-black"
                  style={{ aspectRatio: "9 / 16", maxWidth: 260 }} data-testid="trigger-preview">
                  {canvasVideo ? (
                    <video src={canvasVideo} className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline />
                  ) : project.branding?.cover_image ? (
                    <img src={project.branding.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  ) : null}
                  <TriggerOverlay config={trig} transparent />
                  {!editingDefault && trigCp && (
                    <button data-testid="preview-play-promo" onClick={() => promoPlayRef.current?.()}
                      className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 bg-[#FF2A2A] text-white text-xs font-semibold rounded-full px-4 py-2 hover:scale-105 transition-transform">
                      <Zap className="w-3.5 h-3.5" /> Play promo clip
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* PROMO CLIP — per content piece fragment of the master */}
            {editingDefault ? (
              <div className="mt-8 rounded-2xl border border-white/10 p-6 text-sm text-[#8a8a8a]" data-testid="promo-clip-hint">
                <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#FF2A2A]" /> Enter a <span className="text-white">content piece</span> above (override) to configure its Promo Clip.</span>
              </div>
            ) : !trigCp ? (
              <div className="mt-8 rounded-2xl border border-white/10 p-6 text-sm text-[#8a8a8a]" data-testid="promo-clip-hint">
                <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#FF2A2A]" /> Type a <span className="text-white">content piece</span> name above to set its Promo Clip.</span>
              </div>
            ) : (
              <div className="mt-8">
                <PromoClip key={trigCp} slug={slug} contentPiece={trigCp}
                  initialClip={project.promo_clips?.[trigCp]} playRef={promoPlayRef} />
              </div>
            )}

            {/* FANLY LINK — only for link-based trigger types */}
            {isLinkType && (
              <div className="mt-8 rounded-2xl border border-[#FF2A2A]/30 bg-gradient-to-b from-[#0e0f16] to-[#080809] p-6" data-testid="fanly-link-block">
                <div className="flex items-center gap-2 mb-5">
                  <Link2 className="w-4 h-4 text-[#FF2A2A]" />
                  <h4 className="font-display font-bold tracking-tight text-lg">FANLY LINK</h4>
                </div>
                <div className="grid sm:grid-cols-3 gap-4 mb-5">
                  <div>
                    <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Destination</p>
                    <p className="text-sm mt-1">{project.name}</p>
                  </div>
                  <div>
                    <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Platform</p>
                    <p className="text-sm mt-1">{trigPlatform || "— (default)"}</p>
                  </div>
                  <div>
                    <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Content piece</p>
                    <p className="text-sm mt-1">{trigCp || "—"}</p>
                  </div>
                </div>

                {trig.trigger_id ? (
                  <>
                    <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373] mb-2">Generated URL</p>
                    <div className="flex items-center gap-2 bg-black/50 border border-white/10 rounded-lg px-3 py-2 mb-4">
                      <code data-testid="generated-url" className="font-mono-x text-xs text-[#cfcfcf] truncate flex-1">{generatedUrl()}</code>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <button data-testid="copy-link" onClick={copyLink} className="flex items-center gap-2 bg-white text-black rounded-full px-5 py-2.5 text-sm font-semibold hover:scale-[1.02] transition-transform">
                        <Copy className="w-4 h-4" /> Copy Link
                      </button>
                      <a data-testid="open-link" href={generatedUrl()} target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-white/20 rounded-full px-5 py-2.5 text-sm hover:border-[#FF2A2A] transition-colors">
                        <ExternalLink className="w-4 h-4" /> Open Link
                      </a>
                    </div>

                    {/* QR — encodes THIS FANLY LINK; a scan counts as a Visit and keeps trigger/content attribution */}
                    <div className="mt-6 pt-6 border-t border-white/10" data-testid="qr-block">
                      <div className="flex items-center gap-2 mb-3">
                        <QrCode className="w-4 h-4 text-[#FF2A2A]" />
                        <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">QR for posters · flyers · merch · stories</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        <button data-testid="qr-preview" onClick={previewQr} className="flex items-center gap-2 border border-white/20 rounded-full px-4 py-2 text-sm hover:border-[#FF2A2A] transition-colors">
                          <QrCode className="w-4 h-4" /> Preview QR
                        </button>
                        <button data-testid="qr-download-png" onClick={() => downloadQr("png")} className="flex items-center gap-2 bg-white text-black rounded-full px-4 py-2 text-sm font-semibold hover:scale-[1.02] transition-transform">
                          Download PNG
                        </button>
                        <button data-testid="qr-download-svg" onClick={() => downloadQr("svg")} className="flex items-center gap-2 border border-white/20 rounded-full px-4 py-2 text-sm hover:border-[#FF2A2A] transition-colors">
                          Download SVG
                        </button>
                      </div>
                      {qrPreview && (
                        <img data-testid="qr-preview-img" src={qrPreview} alt="QR preview" className="mt-4 w-40 h-40 rounded-lg bg-white p-2 border border-white/10" />
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/10">
                      <div data-testid="stat-visits">
                        <p className="font-display text-3xl font-bold">{stat.visits}</p>
                        <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373] mt-1">Visits</p>
                      </div>
                      <div data-testid="stat-unlocks">
                        <p className="font-display text-3xl font-bold">{stat.unlocks}</p>
                        <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373] mt-1">Unlocks</p>
                      </div>
                      <div data-testid="stat-conversion">
                        <p className="font-display text-3xl font-bold text-[#FF2A2A]">{Math.round((stat.conversion_rate || 0) * 100)}%</p>
                        <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373] mt-1">Conversion</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="font-mono-x text-xs text-[#737373]">Save this trigger to generate its trackable link.</p>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
